import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../services/api';
import { getDeviceId } from '../services/device';

// Envoyer systématiquement les cookies (dont le cookie httpOnly du JWT) avec chaque requête.
axios.defaults.withCredentials = true;

const AuthContext = createContext();

const API = `${API_BASE_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier d'abord le fragment d'URL pour l'authentification sociale
    const checkSocialAuth = async () => {
      const fragment = window.location.hash;
      
      if (fragment.includes('session_id=')) {
        // Extraire l'ID de session du fragment d'URL
        const sessionIdMatch = fragment.match(/session_id=([^&]+)/);
        if (sessionIdMatch) {
          const sessionId = sessionIdMatch[1];
          
          try {
            setLoading(true);
            // Envoyer l'ID de session au backend
            const response = await axios.post(`${API}/auth/social/callback`, {
              session_id: sessionId
            }, {
              withCredentials: true,  // Important pour les cookies
              timeout: 8000
            });
            
            if (response.data.success) {
              // Récupérer le profil utilisateur après l'authentification sociale
              await fetchSocialUserProfile();
              
              // Nettoyer l'URL
              window.history.replaceState({}, document.title, window.location.pathname);
              
              console.log('✅ Authentification sociale réussie');
              return true; // Authentification sociale réussie
            }
          } catch (error) {
            console.error('❌ Erreur lors de l\'authentification sociale:', error);
            // Nettoyer l'URL même en cas d'erreur
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      }
      
      return false; // Pas d'authentification sociale
      // Mode Aperçu: si activé, ne rien hydrater pour simuler un visiteur externe
      const previewMode = localStorage.getItem('padaid_preview_mode') === 'true';
      if (previewMode) {
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

    };

    // Fonction principale d'initialisation
    const initializeAuth = async () => {
      // D'abord, vérifier l'authentification sociale
      const socialAuthSuccess = await checkSocialAuth();
      
      if (!socialAuthSuccess) {
        // Authentification via cookie httpOnly (JWT). On tente de récupérer le profil :
        // si le cookie est valide, l'utilisateur est réhydraté sans stocker de token dans le navigateur.
        try {
          const response = await axios.get(`${API}/user/profile`, { timeout: 8000 });
          setUser({ ...response.data, authenticated: true, type: 'user' });
          setToken('cookie_session');
        } catch (e) {
          // Pas de session JWT active : vérifier une éventuelle session sociale (cookie)
          await fetchSocialUserProfile();
        }
      }
      
      setLoading(false);
    };

    // Filet de sécurité: quoi qu'il arrive (requête réseau bloquée, etc.),
    // on cesse d'afficher l'écran de chargement au bout de 10s pour ne jamais figer l'app.
    const safetyTimer = setTimeout(() => setLoading(false), 10000);
    initializeAuth().finally(() => clearTimeout(safetyTimer));
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setUser({ ...response.data, authenticated: true, type: 'user' });
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
      // Token invalide, nettoyer
      localStorage.removeItem('padaid_user_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
    }
  };

  const fetchSocialUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/social/profile`, {
        withCredentials: true,  // Important pour les cookies de session
        timeout: 8000
      });
      
      setUser({ 
        ...response.data, 
        authenticated: true, 
        type: 'user',
        social_login: response.data.social_login 
      });
      
      // Pour les logins sociaux, pas de token JWT nécessaire
      setToken('social_session');
      
    } catch (error) {
      // Pas de session active, c'est normal
      console.log('Aucune session sociale active');
    }
  };

  // Connexion admin (ancien système)
  const adminLogin = async (username, password, totpCode = null) => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
        totp_code: totpCode
      });

      const { access_token, requires_2fa } = response.data;

      if (requires_2fa) {
        return { requires2FA: true };
      }

      // Connexion réussie (token en mémoire pour la session, pas de localStorage)
      setToken(access_token);
      setUser({ username, authenticated: true, type: 'admin' });
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur de connexion');
    }
  };

  // Connexion utilisateur (nouveau système)
  const login = async (email, password, totpCode = null, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/user/login`, {
        email,
        password,
        totp_code: totpCode,
        device_id: getDeviceId(),
        remember_me: !!rememberMe
      });

      const { access_token, requires_2fa } = response.data;

      if (requires_2fa) {
        return { requires2FA: true };
      }

      // Connexion réussie (le JWT est posé dans un cookie httpOnly côté serveur).
      // On garde le token en mémoire pour la session courante uniquement (pas de localStorage).
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Tenter de récupérer le profil, mais ne pas bloquer en cas d'erreur (évite les "Not Found")
      try {
        const profileRes = await axios.get(`${API}/user/profile`);
        setUser({ ...profileRes.data, authenticated: true, type: 'user' });
        return { success: true, user_type: profileRes.data.user_type };
      } catch (e) {
        // Profil indisponible pour le moment: définir un utilisateur minimal et laisser l'admin accessible
        setUser({ email, authenticated: true, type: 'user' });
        return { success: true, user_type: 'user' };
      }
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur de connexion');
    }
  };

  // Inscription admin (ancien système)
  const adminRegister = async (username, email, password) => {
    try {
      const response = await axios.post(`${API}/admin/register`, {
        username,
        email,
        password
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
  };

  // Inscription utilisateur (nouveau système)
  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/user/register`, { ...userData, device_id: getDeviceId() });
      console.log('✅ Inscription réussie:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erreur inscription:', error.response?.data);
      // Si l'erreur est une 200 qui a été mal interprétée
      if (error.response?.status === 200) {
        console.log('✅ Inscription réussie malgré l\'erreur apparente');
        return error.response.data;
      }
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Erreur lors de la création du compte');
    }
  };

  // Configuration 2FA admin
  const adminSetup2FA = async (totpCode) => {
    try {
      const response = await axios.post(`${API}/admin/setup-2fa`, {
        totp_code: totpCode
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur lors de la configuration 2FA');
    }
  };

  // Configuration 2FA utilisateur
  const setup2FA = async (totpCode) => {
    try {
      const response = await axios.post(`${API}/user/setup-2fa`, {
        totp_code: totpCode
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur lors de la configuration 2FA');
    }
  };

  // Récupérer le QR code 2FA pour l'utilisateur
  const get2FAQR = async () => {
    try {
      const response = await axios.get(`${API}/user/2fa/qr`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération du QR code');
    }
  };

  // Fonction pour initier l'authentification sociale
  const socialLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const emergentAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/profile')}`;

    // Rediriger vers l'authentification Emergent
    window.location.href = emergentAuthUrl;
  };

  const logout = async () => {
    // Toujours tenter la déconnexion sociale : supprime la session en base ET le cookie,
    // même si le drapeau social_login n'était pas positionné (sans risque si pas de session).
    try {
      await axios.post(`${API}/auth/social/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Erreur lors de la déconnexion sociale:', error);
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('padaid_admin_token');
    localStorage.removeItem('padaid_user_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  // Heartbeat: signale l'activité pour calculer la durée de connexion (toutes les 60s)
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      axios.post(`${API}/user/heartbeat`, {}, { withCredentials: true }).catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    token,
    login,
    adminLogin,
    register,
    adminRegister,
    setup2FA,
    adminSetup2FA,
    get2FAQR,
    socialLogin,
    logout,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};